import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { LegalStrip } from "@/components/AppShell";
import { Button, TextField, Logo } from "@/sentinel";
import { LoginFooter } from "@/pages/Login";

export default function ForcePasswordChange() {
  const { user, setUser, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  // Inline echo of the toast, on the field it concerns.
  const [fieldErr, setFieldErr] = useState({ field: null, msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setFieldErr({ field: null, msg: "" });
    if (next !== confirm) { toast.error("New passwords don't match."); setFieldErr({ field: "confirm", msg: "New passwords don't match." }); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); setFieldErr({ field: "next", msg: "New password must be at least 8 characters." }); return; }
    setBusy(true);
    try {
      const res = await api.changePassword(current, next);
      // Changing the password revokes every old token; keep this browser signed in.
      if (res?.token) localStorage.setItem("sentinel_token", res.token);
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated — welcome to Sentinel.");
    } catch (e) {
      const msg = fmtErr(e.response?.data?.detail) || e.message;
      toast.error(msg);
      setFieldErr({ field: "current", msg });
    } finally {
      setBusy(false);
    }
  };
  const errFor = (f) => (fieldErr.field === f ? fieldErr.msg : undefined);

  return (
    <div className="sn-app sn-root" style={{ minHeight: "100vh" }} data-testid="force-pw-screen">
      <LegalStrip />
      <div className="sn-login" style={{ flex: 1 }}>
        <div className="sn-login-card sn-stack" style={{ gap: "var(--space-6)" }}>
          <div className="sn-stack" style={{ gap: "var(--space-3)", alignItems: "flex-start" }}>
            <Logo size={28} />
            <div>
              <h1 className="text-title" style={{ margin: 0 }}>Set a new password</h1>
              <p className="sn-muted" style={{ margin: "var(--space-1) 0 0" }}>
                Welcome, <b style={{ color: "var(--text-primary)", fontWeight: 600 }}>{user?.name || user?.email}</b>. Your account uses a one-time temporary
                password. Choose a new one to finish setting up; the emailed password will stop working.
              </p>
            </div>
          </div>

          <div className="sn-panel" style={{ padding: "var(--space-6)" }}>
            <form onSubmit={submit} className="sn-stack" style={{ gap: "var(--space-4)" }}>
              <TextField label="Temporary password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)}
                required error={errFor("current")} id="force-pw-current" data-testid="force-pw-current" />
              <TextField label="New password" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)}
                required minLength={8} hint="At least 8 characters. A passphrase is fine." error={errFor("next")} id="force-pw-new" data-testid="force-pw-new" />
              <TextField label="Confirm new password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required minLength={8} error={errFor("confirm")} id="force-pw-confirm" data-testid="force-pw-confirm" />
              <Button type="submit" variant="primary" size="lg" block icon="KeyRound" loading={busy} data-testid="force-pw-submit">
                {busy ? "Saving…" : "Set password and continue"}
              </Button>
            </form>
            <Button variant="ghost" block onClick={logout} style={{ marginTop: "var(--space-2)" }} data-testid="force-pw-signout">Sign out</Button>
          </div>

          <LoginFooter />
        </div>
      </div>
    </div>
  );
}
